import React, { useState, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { getEditImpactPreview, type EditImpactPreview } from './templateManagementService';

interface EditImpactPanelProps {
  templateId: string;
  onDismiss?: () => void;
}

const EditImpactPanel: React.FC<EditImpactPanelProps> = ({ templateId, onDismiss }) => {
  const [impact, setImpact] = useState<EditImpactPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getEditImpactPreview(templateId);
        if (!cancelled) setImpact(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load impact preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  if (loading) {
    return (
      <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
        <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent" />
          Loading impact preview...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  if (!impact) return null;

  const totalInheritingUsage = impact.inheriting_variants.reduce((sum, v) => sum + v.usage_count, 0);

  return (
    <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            Editing Jurisdiction Base
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-amber-600 dark:text-amber-400">
            Revision #{impact.revision_number}
          </span>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1 rounded text-amber-400 hover:text-amber-600 dark:hover:text-amber-200 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Inheriting variants */}
        {impact.inheriting_variants.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                Inheriting Variants (will pick up changes)
              </span>
            </div>
            <div className="space-y-1 ml-5.5">
              {impact.inheriting_variants.map((v) => (
                <div key={v.id} className="flex items-center justify-between text-xs text-amber-700 dark:text-amber-300">
                  <span>{v.name}</span>
                  <span className="text-amber-500 dark:text-amber-400">
                    {v.case_subtype?.replace(/_/g, ' ') || v.template_kind.replace(/_/g, ' ')} · {v.usage_count} uses
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-1.5 ml-5.5 text-xs text-amber-600 dark:text-amber-400">
              {totalInheritingUsage} total uses · {impact.active_matter_count} active matters
            </p>
          </div>
        )}

        {impact.inheriting_variants.length === 0 && (
          <div className="flex items-center gap-1.5">
            <InformationCircleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-xs text-amber-700 dark:text-amber-300">
              No inheriting variants — changes affect only this template.
            </span>
          </div>
        )}

        {/* Independent variants */}
        {impact.independent_variants.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <InformationCircleIcon className="h-4 w-4 text-lex-accent dark:text-lex-accent" />
              <span className="text-xs font-semibold text-lex-accent dark:text-lex-accent">
                Independent Variants (unaffected)
              </span>
            </div>
            <div className="ml-5.5 text-xs text-lex-accent dark:text-lex-accent">
              {impact.independent_variants.map(v => v.name).join(', ')}
            </div>
          </div>
        )}

        <p className="text-xs text-amber-600 dark:text-amber-400 italic">
          Changes require a reason and will be logged.
        </p>
      </div>
    </div>
  );
};

export default EditImpactPanel;
